// =============================================================================
// Lightweight client-side A/B assignment
// =============================================================================
// Sticky per visitor (persisted in localStorage), uniform random split, and
// mirrored into the clickstream via the tracker so every event — page_view,
// click, and milestone — is attributable to its arm (metadata.experiment /
// metadata.variant). No backend, no flicker: the first assignment wins for the
// life of the visitor.
//
//   const arm = getVariant("get_started_flow");  // "dashboard" | "progressive"

import { setExperimentContext, registerExperiment } from "./tracker";

const STORE_KEY = "sift_experiments";

function readAll() {
  try {
    return JSON.parse(window.localStorage.getItem(STORE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function writeAll(map) {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(map));
  } catch {
    /* private mode / storage blocked — variant just won't persist */
  }
}

// Assign (once) and return the variant for `name`. `variants` is the list of
// arm labels; defaults to a 50/50 A/B. The first assignment persists so the
// visitor sees a consistent experience across visits.
export function getVariant(name, variants = ["dashboard", "progressive"], { primary = true } = {}) {
  if (typeof window === "undefined") return variants[0];

  /* Force an arm from the URL: ?exp_company_hero_action=need_search
     Deliberately not persisted, so a forced view cannot silently change what
     the visitor is assigned to afterwards, and a link shared by mistake does
     not enrol anybody. It exists so a demo or a QA pass does not require
     typing into the browser console. */
  try {
    const forced = new URLSearchParams(window.location.search).get(`exp_${name}`);
    if (forced && variants.includes(forced)) {
      (primary ? setExperimentContext : registerExperiment)(name, forced);
      return forced;
    }
  } catch { /* no URL to read */ }

  const all = readAll();
  let arm = all[name];
  if (!arm || !variants.includes(arm)) {
    arm = variants[Math.floor(Math.random() * variants.length)];
    all[name] = arm;
    writeAll(all);
  }
  // Tag the clickstream so this visitor's events carry the arm from here on.
  // `primary: false` for a second concurrent test, so it rides along as
  // `exp_<name>` instead of taking over the legacy `experiment` / `variant`
  // keys that the original test's queries read.
  (primary ? setExperimentContext : registerExperiment)(name, arm);
  return arm;
}

// Read the already-assigned variant without assigning one. Returns null if the
// visitor hasn't entered the experiment yet.
export function peekVariant(name) {
  if (typeof window === "undefined") return null;
  return readAll()[name] || null;
}

// The primary onboarding experiment. Kept here so App can re-establish the
// clickstream tag on load without re-assigning (peek, don't assign).
export const GET_STARTED_EXPERIMENT = "get_started_flow";

/* The company hero test, opened 2026-08-28, to run 30 days.

   Same slot beside the hero copy, two different answers to the question a
   company arrives with. `video` is the 1:53 walkthrough. `availability` is real
   people who have posted, rotating.

   Read it honestly. The company side put 194 into the flow over 30 days and
   produced 6 accounts, so at a 50/50 split each arm gets roughly 97 visitors
   and about 3 accounts. Three against four is not a result, it is a coin. The
   number with enough volume to move in a month is hero CTA clicks, which is why
   `hero_cta_click` is recorded per arm and reported alongside sign-ups rather
   than instead of them. */
export const COMPANY_HERO_EXPERIMENT = "company_hero";
export const COMPANY_HERO_ARMS = ["video", "availability"];

// Re-establish the clickstream experiment tag on app load for a visitor who was
// already assigned in a prior session. Without this, events fired after a full
// page reload (before they re-enter /get-started) would lose the variant tag.
export function rehydrateExperimentContext() {
  const arm = peekVariant(GET_STARTED_EXPERIMENT);
  if (arm) setExperimentContext(GET_STARTED_EXPERIMENT, arm);
  // The individual start test is read past signup (did they post?), so its tag
  // has to survive the reload into the dashboard too.
  const flow = peekVariant(COMPANY_FLOW_EXPERIMENT);
  if (flow && COMPANY_FLOW_ARMS.includes(flow)) registerExperiment(COMPANY_FLOW_EXPERIMENT, flow);
  const start = peekVariant(INDIVIDUAL_START_EXPERIMENT);
  if (start && INDIVIDUAL_START_ARMS.includes(start)) registerExperiment(INDIVIDUAL_START_EXPERIMENT, start);
  // Read past the homepage too: the signup it is scored on happens later.
  const home = peekVariant(HOMEPAGE_EXPERIMENT);
  if (home && HOMEPAGE_ARMS.includes(home)) registerExperiment(HOMEPAGE_EXPERIMENT, home);
}

/* The homepage, opened 2026-10-31.

   `business` is the business landing page that ran through October (still
   served at /employers for ads). `market` is the browsable marketplace,
   pages/MarketHome: people, services and jobs in one list. 50/50, sticky.

   Only for visitors on Sift's own site: partner cream-label canvases keep the
   business page and the native app never shows a homepage. Scored per visitor
   in onboarding/homepage_test.py: landed, signed up (either side), posted.
   Force an arm with ?exp_homepage=market for a demo. */
export const HOMEPAGE_EXPERIMENT = "homepage";
export const HOMEPAGE_ARMS = ["business", "market"];
export function homepageArm() {
  return getVariant(HOMEPAGE_EXPERIMENT, HOMEPAGE_ARMS, { primary: false });
}

/* The individual signup first screen, opened 2026-10-04.

   `resume_first` is the screen that ran through September: "Start with your
   resume", the upload first, and a one-line "No resume handy? Type what you
   do" underneath. `resume_or_describe` is the rebuild: "Be your own boss. Control
   your schedule.", the resume and "Tell us what type of work you want to do"
   as two equal ways in, and a gold Continue that appears once there's text.

   Scored per visitor in analytics/views.py (_individual_start_experiment):
   saw the screen, asked for a post, signed up, posted. About 1,950 a month
   reach this screen, so each arm gets roughly 975 and a two-week read is
   honest on "asked for a post" and "signed up"; "posted" needs longer.

   CLOSED 2026-10-19, `resume_or_describe` for everyone. At 545 / 561 per arm
   it led every step: asked for a post 44.4% vs 39.8%, signed up 38.5% vs 35%,
   posted 21.7% vs 20%, and fewer skipped straight to an account (11.4% vs
   16.3%). Not significant at that size, but it all pointed one way. As with
   company_signup_shape, one value left in the list re-assigns anyone stored
   on `resume_first`, so the old screen is unreachable. */
export const INDIVIDUAL_START_EXPERIMENT = "individual_start";
export const INDIVIDUAL_START_ARMS = ["resume_or_describe"];

/* The company signup flow, opened 2026-10-04: the flow production runs
   against the one designed in the Company Journey Mockups canvas.

   `current` is production as of the 10-02 build: the ready card says
   "Add my details & save my post", the account step asks for your name,
   work email and company name (required), there is no founding card and no
   one-tap sign-in, and the post goes live on signup.

   `new` is the mockup flow. Screen 1 of 2: the post, "N people on Sift fit
   this", Everything is included, gold Continue. Screen 2 of 2: Founding
   Organization terms with the spot number, one-tap sign-in, email only, and
   "Your post is saved, never posted without your permission": the post is a
   draft until they publish it from the guided home.

   GetStartedPreview routes on the arm: `current` gets CompanyDoors (then
   CompanyNeedComposer and GetStartedCompany, which shows production's three
   boxes in this arm), `new` gets GetStartedNeedFirst's business flow, which
   until now no route reached. Scored per visitor in onboarding/company_flow_test.py:
   started, saw the post, reached the account step, signed up, published a
   post, said who they are. About 85 companies a month reach the account
   step, so give it a month before reading anything past sign-ups. */
export const COMPANY_FLOW_EXPERIMENT = "company_signup_flow";
export const COMPANY_FLOW_ARMS = ["current", "new"];

/* The company signup shape, opened 2026-09-05.

   `doors` is the incumbent: three things we can do for you. `scan` asks
   whether you have jobs posted, reads them off your applicant tracking
   system, and answers with the people on the board who match one.

   Rides along as a second concurrent test (`primary: false`) so it does not
   take over the flat `experiment` / `variant` keys the get_started_flow
   queries still read. */
export const COMPANY_SIGNUP_EXPERIMENT = "company_signup_shape";
/* "scan" retired 2026-09-18: reading a company's own job board and
   answering with matching people was a good idea that did not work well
   enough to keep in front of every other visitor. Leaving the arm list at
   one value means getVariant also re-assigns anyone previously stuck on
   it, so nobody is left mid-flow in a screen that no longer exists. */
export const COMPANY_SIGNUP_ARMS = ["doors"];

/* company_hero_action: RETIRED 2026-09-08, shipped at 100%.
   `need_search` -- a field that looks like a search bar and starts a posting --
   replaced the pain headline and two buttons for everybody, on the owner's
   call. It never ran long enough to produce a read; the decision was that the
   incumbent had already been measured (3.1% of organisations reaching an
   account against 21% of individuals) and was not worth splitting traffic
   against any longer.

   The constants are gone rather than left assigned-but-unread, so nobody
   inherits an arm from storage that nothing renders. */
